import { useCallback } from 'react';

import { trpc } from '@/services/trpc';

/**
 * The feed-wide half of the attribute lifecycle: rename, merge, and switch an
 * attribute off or back on for the whole catalog.
 *
 * Switching off is deliberately not a delete. The row keeps its values and its
 * aliases, and its name is fed back to the enrichment model as a
 * do-not-extract instruction — deleting would throw away both, so the next
 * import would simply propose the same name again.
 */
export function useFeedAttributeMutation() {
  const utils = trpc.useUtils();

  const invalidate = useCallback(async () => {
    await Promise.all([
      utils.shopProductAttributes.listByFeed.invalidate(),
      // A merge re-points values, and a deactivation changes how the product
      // detail renders its rows.
      utils.shopProducts.findOne.invalidate(),
    ]);
  }, [utils]);

  const update = trpc.shopProductAttributes.update.useMutation({
    onSuccess: invalidate,
  });
  const merge = trpc.shopProductAttributes.merge.useMutation({
    onSuccess: invalidate,
  });

  const { mutateAsync: updateAsync } = update;

  const rename = useCallback(
    ({
      id,
      name,
      displayName,
    }: {
      id: string;
      name: string;
      displayName: string | null;
    }) => updateAsync({ id, name, displayName }),
    [updateAsync],
  );

  const setActive = useCallback(
    ({ id, active }: { id: string; active: boolean }) =>
      updateAsync({ id, active }),
    [updateAsync],
  );

  return {
    update,
    merge,
    rename,
    setActive,
  };
}
